// Iter46 — Admin → SLA Rules page (severity × tenant-pair overrides).
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useTenants } from "../api/sitroom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITIES = ["s1", "s2", "s3", "s4"];

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

export default function AdminSlaRules() {
  const addToast = useStore((s) => s.addToast);
  const [rules, setRules] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [editing, setEditing] = useState(null); // null|"new"|<id>
  const { data: tenants = [] } = useTenants();

  const load = async () => {
    try {
      const [r, d] = await Promise.all([
        axios.get(`${API}/admin/sla-rules`),
        axios.get(`${API}/admin/sla-rules/defaults`),
      ]);
      setRules(r.data || []);
      setDefaults(d.data || {});
    } catch (e) {
      addToast({ type: "error", title: "Failed to load SLA rules", body: e?.response?.data?.detail || e.message });
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-sla-rules">
      <div
        className="rounded-md p-4 mb-4"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
      >
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase mb-2">Default SLA matrix (§3.6)</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {SEVERITIES.map((s) => (
            <div key={s} className="p-2 rounded"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
              <div style={mono(11, "#9ec3ec", 700, "0.4px")} className="uppercase">{s}</div>
              <div className="mt-1" style={mono(11, "var(--text, #f0f2f8)")}>
                Ack <b>{defaults[s]?.ack_minutes ?? "—"}m</b> · Resolve <b>{defaults[s]?.resolve_minutes ?? "—"}m</b>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2" style={mono(9, "var(--dimmer, #9198b0)")}>
          Pair-specific overrides below replace these for matching (severity × tenant pair) incidents only. Existing incidents keep their SLA snapshot.
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">Pair-specific rules</div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="px-2.5 py-1 rounded"
          style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
          data-testid="admin-sla-new-btn"
        >+ Add rule</button>
      </div>

      <div className="rounded overflow-hidden"
        style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)" }}>
        <div className="grid grid-cols-12 gap-2 px-3 py-2"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", ...mono(9, "var(--dimmer, #9198b0)", 600, "0.8px") }}>
          <div className="col-span-1 uppercase">Severity</div>
          <div className="col-span-3 uppercase">Tenant A</div>
          <div className="col-span-3 uppercase">Tenant B</div>
          <div className="col-span-2 uppercase text-right">Ack (min)</div>
          <div className="col-span-2 uppercase text-right">Resolve (min)</div>
          <div className="col-span-1"></div>
        </div>
        {rules.length === 0 && editing !== "new" ? (
          <div className="px-3 py-3" style={mono(11, "var(--dimmer, #9198b0)")} data-testid="admin-sla-empty">
            No pair-specific rules yet. The default matrix above applies to all incidents.
          </div>
        ) : rules.map((r) => (
          <RuleRow key={r.id} row={r} tenants={tenants}
            isEditing={editing === r.id}
            onEdit={() => setEditing(r.id)}
            onCancel={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await load(); }} />
        ))}
        {editing === "new" && (
          <RuleRow row={null} tenants={tenants}
            isEditing={true}
            onEdit={() => {}}
            onCancel={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await load(); }} />
        )}
      </div>
    </div>
  );
}

const RuleRow = ({ row, tenants, isEditing, onEdit, onCancel, onSaved }) => {
  const addToast = useStore((s) => s.addToast);
  const [severity, setSeverity] = useState(row?.severity || "s1");
  const [tenantA, setTenantA] = useState(row?.tenant_a || "");
  const [tenantB, setTenantB] = useState(row?.tenant_b || "");
  const [ack, setAck] = useState(row?.ack_minutes != null ? String(row.ack_minutes) : "");
  const [resolve, setResolve] = useState(row?.resolve_minutes != null ? String(row.resolve_minutes) : "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!severity || !ack || !resolve) {
      addToast({ type: "error", title: "Missing fields", body: "Severity, ack and resolve minutes are required." });
      return;
    }
    const body = {
      severity,
      tenant_a: tenantA || null,
      tenant_b: tenantB || null,
      ack_minutes: parseInt(ack, 10),
      resolve_minutes: parseInt(resolve, 10),
    };
    setBusy(true);
    try {
      if (row?.id) await axios.patch(`${API}/admin/sla-rules/${row.id}`, body);
      else await axios.post(`${API}/admin/sla-rules`, body);
      addToast({ type: "success", title: row?.id ? "SLA rule updated" : "SLA rule added" });
      onSaved();
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!row?.id) return;
    if (!window.confirm("Delete this SLA rule?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/sla-rules/${row.id}`);
      addToast({ type: "success", title: "SLA rule deleted" });
      onSaved();
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (isEditing) {
    return (
      <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center"
        style={{ borderTop: "1px solid rgba(255,255,255,0.04)", background: "rgba(24,95,165,0.06)" }}
        data-testid={row?.id ? `admin-sla-edit-${row.id}` : "admin-sla-edit-new"}>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)}
          className="col-span-1 px-2 py-1.5 rounded outline-none"
          style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
          data-testid="admin-sla-edit-severity">
          {SEVERITIES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
        <TenantSelect className="col-span-3" value={tenantA} onChange={setTenantA} tenants={tenants} testId="admin-sla-edit-tenant-a" />
        <TenantSelect className="col-span-3" value={tenantB} onChange={setTenantB} tenants={tenants} testId="admin-sla-edit-tenant-b" />
        <input type="number" value={ack} onChange={(e) => setAck(e.target.value)}
          className="col-span-2 px-2 py-1.5 rounded outline-none text-right"
          style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
          data-testid="admin-sla-edit-ack" />
        <input type="number" value={resolve} onChange={(e) => setResolve(e.target.value)}
          className="col-span-2 px-2 py-1.5 rounded outline-none text-right"
          style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
          data-testid="admin-sla-edit-resolve" />
        <div className="col-span-1 flex justify-end gap-1">
          <button type="button" onClick={save} disabled={busy}
            className="px-2 py-1 rounded"
            style={{ background: "#185FA5", color: "#fff", ...mono(9, undefined, 700, "0.4px"), border: "none", cursor: busy ? "wait" : "pointer" }}
            data-testid="admin-sla-edit-save">✓</button>
          <button type="button" onClick={onCancel}
            className="px-2 py-1 rounded"
            style={{ background: "transparent", color: "var(--dimmer, #9198b0)", ...mono(9, undefined, 700, "0.4px"), border: "1px solid rgba(255,255,255,0.14)", cursor: "pointer" }}
            data-testid="admin-sla-edit-cancel">×</button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center"
      style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}
      data-testid={`admin-sla-row-${row.id}`}>
      <div className="col-span-1" style={mono(11, "#9ec3ec", 700)}>{row.severity.toUpperCase()}</div>
      <div className="col-span-3 truncate" style={mono(11, "var(--text, #f0f2f8)")}>{row.tenant_a || "— any —"}</div>
      <div className="col-span-3 truncate" style={mono(11, "var(--text, #f0f2f8)")}>{row.tenant_b || "— any —"}</div>
      <div className="col-span-2 text-right" style={mono(11, "var(--text, #f0f2f8)", 700)}>{row.ack_minutes}m</div>
      <div className="col-span-2 text-right" style={mono(11, "var(--text, #f0f2f8)", 700)}>{row.resolve_minutes}m</div>
      <div className="col-span-1 flex justify-end gap-1">
        <button type="button" onClick={onEdit}
          className="px-2 py-1 rounded"
          style={{ background: "transparent", color: "#9ec3ec", border: "1px solid rgba(24,95,165,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
          data-testid={`admin-sla-row-edit-${row.id}`}>Edit</button>
        <button type="button" onClick={del} disabled={busy}
          className="px-2 py-1 rounded"
          style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
          data-testid={`admin-sla-row-delete-${row.id}`}>Del</button>
      </div>
    </div>
  );
};

const TenantSelect = ({ value, onChange, tenants, className, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    className={`${className} px-2 py-1.5 rounded outline-none`}
    style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
    data-testid={testId}>
    <option value="">— any tenant —</option>
    {tenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
  </select>
);
